import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, LayoutDashboard, Cpu, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import CustomCursor from './CustomCursor';
import Header from './Header';
import Footer from './Footer';
import ProjectEstimator from './ProjectEstimator';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

const services = [
  {
    icon: Code,
    title: 'Sites web sur-mesure',
    desc: 'Création de sites vitrines professionnels, performants et conçus pour convertir.',
    price: '800€',
  },
  {
    icon: LayoutDashboard,
    title: 'Applications web',
    desc: "Développement d'applications web complètes, dashboards, SaaS ou outils métier.",
    price: '3\u00A0000€',
  },
  {
    icon: Cpu,
    title: 'Automatisation & Intelligence Artificielle',
    desc: "Mise en place de systèmes automatisés, intégrations API, workflows et solutions IA.",
    price: '1\u00A0500€',
  },
];

const includes = [
  'Développement 100% sur-mesure',
  'Performance et optimisation',
  'Design moderne et responsive',
  'Intégrations spécifiques selon vos besoins',
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      className="group relative border border-[#1a1a1a] rounded-xl p-8 sm:p-10 hover:border-[#2A3B8F]/40 transition-colors duration-500"
      data-testid={`service-card-${index}`}
    >
      <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-[#2A3B8F]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative">
        <div className="w-10 h-10 rounded-lg bg-[#2A3B8F]/10 flex items-center justify-center mb-6">
          <Icon size={20} className="text-[#2A3B8F]" />
        </div>
        <h3 className="text-white text-lg font-medium mb-3">{service.title}</h3>
        <p className="text-[#888] text-sm leading-relaxed mb-6">{service.desc}</p>
        <p className="text-white text-xl sm:text-2xl font-semibold tracking-tight whitespace-nowrap">
          À partir de {service.price}
        </p>
      </div>
    </motion.div>
  );
};

const TarifsPage = () => {
  const introRef = useRef(null);
  const includesRef = useRef(null);
  const importantRef = useRef(null);

  const introInView = useInView(introRef, { once: true, margin: '-10%' });
  const includesInView = useInView(includesRef, { once: true, margin: '-10%' });
  const importantInView = useInView(importantRef, { once: true, margin: '-10%' });

  return (
    <div className="kairos-app grain-overlay">
      <Helmet>
        <title>Tarifs développement web & IA — KairosLab</title>
        <meta name="description" content="Découvrez les tarifs pour un site web, une application ou une automatisation IA sur-mesure. Devis rapide sous 24h." />
        <link rel="canonical" href="https://kairoslab.dev/tarifs" />
        <meta property="og:title" content="Tarifs développement web & IA — KairosLab" />
        <meta property="og:description" content="Découvrez les tarifs pour un site web, une application ou une automatisation IA sur-mesure. Devis rapide sous 24h." />
        <meta property="og:url" content="https://kairoslab.dev/tarifs" />
      </Helmet>

      <CustomCursor />
      <Header />

      <main>
        {/* Hero */}
        <section className="min-h-[50vh] flex items-end pb-16 pt-32 px-[5%]">
          <div className="max-w-3xl mx-auto w-full">
            <motion.p
              className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              Investissement
            </motion.p>
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="tarifs-title"
            >
              Combien coûte un projet ?
            </motion.h1>
          </div>
        </section>

        {/* Introduction */}
        <section ref={introRef} className="px-[5%] pb-24">
          <motion.div
            className="max-w-3xl mx-auto"
            variants={fadeUp}
            initial="hidden"
            animate={introInView ? 'visible' : 'hidden'}
          >
            <p className="text-[#b0b0b0] text-base sm:text-lg leading-relaxed">
              Chaque projet que nous réalisons est entièrement sur-mesure.
              <br />
              Nous ne proposons pas de solutions standardisées ou de templates.
            </p>
            <p className="text-[#888] text-base sm:text-lg leading-relaxed mt-6">
              Cependant, pour vous donner une idée, voici des ordres de grandeur :
            </p>
          </motion.div>
        </section>

        {/* Service Cards */}
        <section className="px-[5%] pb-32" data-testid="tarifs-services">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <ServiceCard key={s.title} service={s} index={i} />
            ))}
          </div>
        </section>

        {/* Includes */}
        <section ref={includesRef} className="px-[5%] pb-32">
          <div className="max-w-3xl mx-auto">
            <motion.h2
              className="text-white text-lg font-medium mb-8"
              variants={fadeUp}
              initial="hidden"
              animate={includesInView ? 'visible' : 'hidden'}
            >
              Ce que ces tarifs incluent :
            </motion.h2>
            <div className="space-y-4">
              {includes.map((item, i) => (
                <motion.div
                  key={item}
                  className="flex items-center gap-4"
                  custom={i + 1}
                  variants={fadeUp}
                  initial="hidden"
                  animate={includesInView ? 'visible' : 'hidden'}
                >
                  <div className="w-5 h-5 rounded-full bg-[#2A3B8F]/15 flex items-center justify-center flex-shrink-0">
                    <Check size={12} className="text-[#2A3B8F]" />
                  </div>
                  <span className="text-[#b0b0b0] text-sm sm:text-base">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Important */}
        <section ref={importantRef} className="px-[5%] pb-32">
          <motion.div
            className="max-w-3xl mx-auto border-l-2 border-[#2A3B8F] pl-8"
            variants={fadeUp}
            initial="hidden"
            animate={importantInView ? 'visible' : 'hidden'}
          >
            <p className="text-white text-base leading-relaxed mb-4">
              Chaque projet est unique.
              <br />
              Un devis précis est établi après un échange rapide.
            </p>
            <p className="text-[#888] text-sm leading-relaxed mb-4">
              Nous privilégions des collaborations sérieuses avec des clients souhaitant investir dans des solutions durables et performantes.
            </p>
            <p className="text-[#2A3B8F] text-sm font-medium">
              Budget minimum recommandé : 800€
            </p>
          </motion.div>
        </section>

        {/* Separator */}
        <div className="flex justify-center pb-16">
          <div className="flex items-center gap-3">
            <div className="w-16 h-px bg-[#1a1a1a]" />
            <div className="w-1.5 h-1.5 bg-[#2A3B8F] rotate-45" />
            <div className="w-16 h-px bg-[#1a1a1a]" />
          </div>
        </div>

        {/* Estimator */}
        <ProjectEstimator />
      </main>

      <Footer />
    </div>
  );
};

export default TarifsPage;
